import { Link } from "react-router-dom";
import { BadgeCheck, Check } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import TopBadge from "@/components/TopBadge";
import TopBadgeShareButton from "@/components/top-badge/TopBadgeShareButton";

// One campaign entry: video, creator, live tally, share link and the single vote button.
export default function TopBadgeEntryCard({ entry, rank, canVote, votedHere, hasVoted, onVote, busy, highlighted = false }) {
  const u = entry.user;
  return (
    <article id={`entry-${entry.id}`} className={`card-surface overflow-hidden flex flex-col ${highlighted ? "ring-2 ring-yellow-400 shadow-[0_0_32px_rgba(250,204,21,0.25)]" : ""}`} data-testid={`top-badge-entry-${entry.id}`}>
      <div className="relative bg-black aspect-[9/12] sm:aspect-video">
        <video src={entry.video_url} controls playsInline preload="metadata" className="absolute inset-0 w-full h-full object-contain" data-testid="top-badge-entry-video" />
        <span className={`absolute top-2 left-2 px-2 py-0.5 text-[10px] font-display ${rank <= 5 ? "bg-yellow-400 text-black" : "bg-black/70 text-zinc-300 border border-white/10"}`} data-testid="top-badge-entry-rank">#{rank}</span>
      </div>
      <div className="p-3 flex items-center gap-3">
        <Link to={`/u/${u.id}`} className="shrink-0">
          <Avatar className="w-9 h-9 border border-white/10"><AvatarImage src={u.avatar_url} /><AvatarFallback className="bg-zinc-800 text-white text-xs">{u.display_name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-x-1 gap-y-0.5 text-sm text-white min-w-0"><Link to={`/u/${u.id}`} className="truncate max-w-full hover:underline" data-testid="top-badge-entry-name">{u.display_name}</Link>{u.is_verified && <BadgeCheck size={12} className="text-amber-400 shrink-0" />}<TopBadge show={u.top_badge} size="xs" /></div>
          {entry.caption && <div className="text-xs text-zinc-400 truncate" data-testid="top-badge-entry-caption">{entry.caption}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-xl text-white tabular-nums" data-testid="top-badge-entry-votes">{entry.votes}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">vote{entry.votes === 1 ? "" : "s"}</div>
        </div>
      </div>
      <div className="px-3 pb-3 flex flex-col gap-2">
        <div className="min-w-0">
        {votedHere ? (
          <div className="w-full text-center text-xs py-2 border border-yellow-400/50 text-yellow-200 bg-yellow-400/10" data-testid="top-badge-entry-voted"><Check size={12} className="inline mr-1" />Your vote</div>
        ) : entry.is_mine ? (
          <div className="w-full text-center text-xs py-2 border border-white/10 text-zinc-500" data-testid="top-badge-entry-mine">Your entry</div>
        ) : (
          <Button size="sm" className="w-full bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-40" disabled={!canVote || hasVoted || busy} onClick={() => onVote(entry.id)} data-testid="top-badge-entry-vote-btn">
            {hasVoted ? "Vote used" : canVote ? "Vote" : "Voting closed"}
          </Button>
        )}
        </div>
        <TopBadgeShareButton entryId={entry.id} name={u.display_name} className="w-full border border-white/10 text-zinc-300 hover:text-white" label={entry.is_mine ? "Share my entry" : "Share"} />
      </div>
    </article>
  );
}
